import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { toast } from 'sonner';

export default function Designer() {
  const [preview, setPreview] = useState(null);
  const [activeSection, setActiveSection] = useState('combined');
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [deploying, setDeploying] = useState({});
  const [sectionHtml, setSectionHtml] = useState({});

  useEffect(() => {
    fetchPreview();
  }, []);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/designer/preview`);
      const data = await res.json();
      setPreview(data);
      
      // Fetch all sections HTML
      const sections = ['hero', 'trust', 'products', 'cta-banner', 'full', 'product-page'];
      const htmlData = {};
      
      for (const section of sections) {
        const sectionRes = await fetch(`${API}/designer/${section}`);
        const sectionData = await sectionRes.json();
        htmlData[section] = sectionData;
      }
      
      setSectionHtml(htmlData);
    } catch (err) {
      toast.error('Fehler beim Laden');
    }
    setLoading(false);
  };

  const copyToClipboard = async (text, label) => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert!`);
    } catch (err) {
      toast.error('Kopieren fehlgeschlagen');
    }
    setCopying(false);
  };

  // Deploy a section to Magento Live
  const deploySection = async (sectionType) => {
    setDeploying(prev => ({ ...prev, [sectionType]: true }));
    
    try {
      const res = await fetch(`${API}/designer/deploy/${sectionType}`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`✅ ${sectionType} deployed! ${data.message}`);
        if (data.next_step) {
          toast.info(`📋 Nächster Schritt: ${data.next_step}`);
        }
      } else {
        toast.error(`❌ Deployment fehlgeschlagen: ${data.message}`);
      }
    } catch (err) {
      toast.error('Fehler beim Deployment');
    }
    
    setDeploying(prev => ({ ...prev, [sectionType]: false }));
  };

  // Deploy all sections at once
  const deployAll = async () => {
    setDeploying(prev => ({ ...prev, all: true }));
    
    try {
      const res = await fetch(`${API}/designer/deploy-all`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(`✅ Alle ${data.successful} Sections deployed!`);
        if (data.next_step) {
          toast.info(`📋 ${data.next_step}`);
        }
      } else {
        toast.warning(`${data.successful}/${data.total} Sections deployed`);
        data.results?.forEach(r => {
          if (!r.success) {
            toast.error(`❌ ${r.section}: ${r.message}`);
          }
        });
      }
    } catch (err) {
      toast.error('Fehler beim Deployment');
    }
    
    setDeploying(prev => ({ ...prev, all: false }));
  };

  // Get the API section name for deployment
  const getDeploySectionName = (tabId) => {
    const mapping = {
      'combined': 'combined',
      'hero': 'hero',
      'trust': 'trust',
      'products': 'products',
      'cta-banner': 'cta_banner'
    };
    return mapping[tabId];
  };

  const sections = [
    { id: 'combined', label: 'Alles', icon: '📦' },
    { id: 'hero', label: 'Hero', icon: '🎯' },
    { id: 'trust', label: 'Trust', icon: '✅' },
    { id: 'products', label: 'Produkte', icon: '📋' },
    { id: 'cta-banner', label: 'CTA', icon: '🚀' },
    { id: 'product-page', label: 'Produktseiten', icon: '🛒' },
  ];

  const getSectionData = () => {
    if (activeSection === 'combined') {
      return sectionHtml['full'];
    }
    if (activeSection === 'product-page') {
      return sectionHtml['product-page'];
    }
    return sectionHtml[activeSection];
  };

  const getHtmlContent = () => {
    const data = getSectionData();
    if (!data) return '';
    
    if (activeSection === 'combined') {
      return data.combined_html || '';
    }
    if (activeSection === 'product-page') {
      // Combine all product page elements
      const elements = data.elements || {};
      return Object.values(elements).map(el => el.html).join('\n\n');
    }
    return data.html || '';
  };

  if (loading) {
    return (
      <div className="p-4 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4 animate-spin">⚙️</div>
          <p className="text-gray-500">Design wird generiert...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="designer-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <h1 className="text-xl font-bold text-gray-900">
            Content-Generator
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            HTML-Snippets und Widgets für adagmedia.ch
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Brand Colors */}
        {preview?.brand_colors && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">Markenfarben</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(preview.brand_colors).map(([name, color]) => (
                <div 
                  key={name}
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80"
                  onClick={() => copyToClipboard(color, name)}
                  title="Klicken zum Kopieren"
                >
                  <div 
                    className="w-8 h-8 rounded border border-gray-200"
                    style={{ backgroundColor: color }}
                  />
                  <div>
                    <div className="text-xs text-gray-500">{name.replace(/_/g, ' ')}</div>
                    <div className="text-sm font-mono">{color}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {sections.map(section => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors
                ${activeSection === section.id 
                  ? 'bg-green-600 text-white' 
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              data-testid={`tab-${section.id}`}
            >
              {section.icon} {section.label}
            </button>
          ))}
        </div>

        {/* Preview & Code */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Live Preview */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="font-medium text-gray-900">Live-Vorschau</span>
              <span className="text-xs text-gray-500">Scrollbar für mehr</span>
            </div>
            <div 
              className="p-4 overflow-auto" 
              style={{ maxHeight: '600px' }}
            >
              <div 
                className="border border-dashed border-gray-300 rounded-lg overflow-hidden"
                dangerouslySetInnerHTML={{ __html: getHtmlContent() }}
              />
            </div>
          </div>

          {/* HTML Code */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="font-medium text-gray-900">HTML Code</span>
              <div className="flex gap-2">
                <button
                  onClick={() => copyToClipboard(getHtmlContent(), 'HTML')}
                  disabled={copying}
                  className="px-3 py-1.5 bg-gray-600 text-white rounded text-sm font-medium
                             hover:bg-gray-700 transition-colors disabled:bg-gray-400"
                  data-testid="copy-html-btn"
                >
                  {copying ? '...' : '📋 Kopieren'}
                </button>
                {activeSection !== 'product-page' && (
                  <button
                    onClick={() => deploySection(getDeploySectionName(activeSection))}
                    disabled={deploying[getDeploySectionName(activeSection)] || deploying.all}
                    className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium
                               hover:bg-green-700 transition-colors disabled:bg-green-400"
                    data-testid="deploy-section-btn"
                  >
                    {deploying[getDeploySectionName(activeSection)] ? '⏳ Deploying...' : '🚀 Jetzt umsetzen'}
                  </button>
                )}
              </div>
            </div>
            <div className="p-4">
              <pre 
                className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto text-xs"
                style={{ maxHeight: '540px' }}
              >
                {getHtmlContent()}
              </pre>
            </div>
          </div>
        </div>

        {/* Deployment Instructions */}
        {activeSection === 'combined' && sectionHtml['full']?.deployment_instructions && (
          <div className="mt-6 space-y-4">
            {/* Deploy All Button */}
            <div className="bg-green-50 rounded-lg border-2 border-green-500 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-green-800">
                    🚀 Alles auf einmal deployen
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    Erstellt alle CMS Blocks auf der Live-Seite
                  </p>
                </div>
                <button
                  onClick={deployAll}
                  disabled={deploying.all}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold text-lg
                             hover:bg-green-700 transition-colors disabled:bg-green-400
                             shadow-lg hover:shadow-xl"
                  data-testid="deploy-all-btn"
                >
                  {deploying.all ? '⏳ Deploying...' : '🚀 Jetzt alles umsetzen'}
                </button>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
              <h3 className="font-semibold text-amber-800 mb-2">
                📋 Deployment-Anleitung
              </h3>
              <div className="prose prose-sm text-amber-900">
                <pre className="whitespace-pre-wrap text-sm bg-white p-3 rounded border border-amber-200">
                  {sectionHtml['full'].deployment_instructions}
                </pre>
              </div>
            </div>
          </div>
        )}

        {/* Section Info */}
        {getSectionData()?.description && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              ℹ️ {getSectionData().description}
            </p>
          </div>
        )}

        {/* Products List (if on products tab) */}
        {activeSection === 'products' && preview?.products && (
          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              Verwendete Produkte (verifizierte URLs)
            </h3>
            <div className="space-y-2">
              {preview.products.map((product, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <span className="font-medium text-gray-900">{product.name}</span>
                    <span className="text-gray-500 text-sm ml-2">({product.description})</span>
                  </div>
                  <a 
                    href={`https://adagmedia.ch${product.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 text-sm hover:underline"
                  >
                    {product.url}
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* USPs List (if on trust tab) */}
        {activeSection === 'trust' && preview?.usps && (
          <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-3">
              Verwendete USPs (verifiziert)
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {Object.entries(preview.usps).map(([key, usp]) => (
                <div 
                  key={key}
                  className="p-3 bg-green-50 rounded-lg border border-green-200"
                >
                  <div className="font-medium text-green-800">{usp.title}</div>
                  <div className="text-sm text-green-700">{usp.subtitle}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product Page Elements (if on product-page tab) */}
        {activeSection === 'product-page' && sectionHtml['product-page']?.elements && (
          <div className="mt-6 space-y-4">
            <h3 className="font-semibold text-gray-900">
              Produktseiten-Elemente
            </h3>
            {Object.entries(sectionHtml['product-page'].elements).map(([key, element]) => (
              <div 
                key={key}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-gray-900 capitalize">
                      {key.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      Position: {element.position}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(element.html, key)}
                      disabled={copying}
                      className="px-3 py-1.5 bg-gray-600 text-white rounded text-sm font-medium
                                 hover:bg-gray-700 transition-colors disabled:bg-gray-400"
                    >
                      📋 Kopieren
                    </button>
                    <button
                      onClick={() => deploySection(key)}
                      disabled={deploying[key]}
                      className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium
                                 hover:bg-green-700 transition-colors disabled:bg-green-400"
                      data-testid={`deploy-${key}-btn`}
                    >
                      {deploying[key] ? '⏳...' : '🚀 Umsetzen'}
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-sm text-gray-600 mb-3">{element.description}</p>
                  <div 
                    className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: element.html }}
                  />
                </div>
              </div>
            ))}
            
            {/* Product Page Deployment Instructions */}
            {sectionHtml['product-page']?.deployment_instructions && (
              <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                <h4 className="font-semibold text-amber-800 mb-2">
                  📋 Deployment für Produktseiten
                </h4>
                <pre className="whitespace-pre-wrap text-sm bg-white p-3 rounded border border-amber-200 text-amber-900">
                  {sectionHtml['product-page'].deployment_instructions}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
